/**
 * CSV lead import — docs/modules/leadImport.model.md. Three steps: upload,
 * review the auto-detected column mapping and confirm a duplicate strategy,
 * then watch progress until the run completes. Replaces the "Import is not
 * available" notice `LeadsListPage.jsx`'s Import button used to open.
 */
import { useEffect, useRef, useState } from 'react';
import { Modal, Field, ButtonSpinner } from '../../components/common/index.jsx';
import { createDownloadUrl } from '../../api/files.api.js';
import {
  useUploadCsv, useCreateImport, useValidateImport, useExecuteImport, useImportJob,
} from './useLeadImport.js';

const STRATEGIES = [
  { value: 'skip', label: 'Skip', help: 'Leave an existing matching lead untouched.' },
  { value: 'update', label: 'Update', help: 'Overwrite the existing lead with every mapped value from the row.' },
  { value: 'merge', label: 'Merge', help: 'Fill in only the fields the existing lead has left blank.' },
  { value: 'create_anyway', label: 'Create anyway', help: 'Ignore duplicate matches and always create a new lead.' },
];

const FIELD_LABEL = {
  first_name: 'First name', last_name: 'Last name', email: 'Email', phone: 'Phone', mobile: 'Mobile',
  job_title: 'Job title', company_name: 'Company', website: 'Website', address_line1: 'Address',
  city_text: 'City', postal_code: 'Postal code', rating: 'Rating', estimated_value: 'Estimated value',
  source_detail: 'Source detail', industry: 'Industry', country: 'Country', source: 'Lead source',
  status: 'Status', owner_email: 'Owner (by email)',
};

/**
 * A template, not real data — two rows are enough to show the shape without
 * implying a minimum. `Last Name` and `Company` are the only two columns
 * `crm.lead` actually requires; every other header here is auto-matched by
 * name (imports.constants.js's FIELD_ALIASES) but optional, and Industry/
 * Country/Source/Status are resolved by *name* against the org's own master
 * data — a value that doesn't match anything there is left blank rather than
 * failing the row, so this sample's values are illustrative, not contractual.
 */
const SAMPLE_CSV_HEADERS = [
  'First Name', 'Last Name', 'Email', 'Company', 'Phone', 'Job Title',
  'Industry', 'Country', 'Source', 'Status', 'Rating', 'Estimated Value', 'Owner Email',
];
const SAMPLE_CSV_ROWS = [
  ['Amara', 'Chen', 'amara.chen@cobaltrobotics.example.com', 'Cobalt Robotics', '+1-415-555-0100',
    'VP Engineering', 'Manufacturing', 'United States', 'Web', 'Nurturing', 'Hot', '45000', ''],
  ['Divya', 'Rao', 'divya.rao@brighthealth.example.org', 'Bright Health Labs', '+44-20-7946-0100',
    'Director of Procurement', 'Healthcare', 'United Kingdom', 'Referral', 'Qualified', 'Warm', '18000', ''],
];

function downloadSampleCsv() {
  const esc = (v) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  const text = [SAMPLE_CSV_HEADERS, ...SAMPLE_CSV_ROWS].map((row) => row.map(esc).join(',')).join('\n');
  const url = URL.createObjectURL(new Blob([text], { type: 'text/csv' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = 'lead-import-sample.csv';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function LeadImportWizard({ open, onClose }) {
  const [step, setStep] = useState('upload'); // upload | review | progress
  const [fileName, setFileName] = useState('');
  const [jobId, setJobId] = useState(null);
  const [preview, setPreview] = useState(null); // last validate() response
  const [strategy, setStrategy] = useState('skip');
  const fileInputRef = useRef(null);

  const upload = useUploadCsv();
  const createJob = useCreateImport();
  const validate = useValidateImport();
  const execute = useExecuteImport();
  const job = useImportJob(jobId, { enabled: step === 'progress' });

  useEffect(() => {
    if (!open) {
      setStep('upload'); setFileName(''); setJobId(null); setPreview(null); setStrategy('skip');
    }
  }, [open]);

  if (!open) return null;

  const onFile = async (file) => {
    if (!file) return;
    setFileName(file.name);
    try {
      const objectId = await upload.mutateAsync(file);
      const created = await createJob.mutateAsync({ entity_key: 'lead', source_object_id: objectId, duplicate_strategy: strategy });
      setJobId(created.id);
      const result = await validate.mutateAsync({ id: created.id, body: {} });
      setPreview(result);
      setStep('review');
    } catch {
      // Toasted by the hooks already — stay on the upload step so they can retry.
    }
  };

  const onRunImport = async () => {
    await execute.mutateAsync({ id: jobId, body: { duplicate_strategy: strategy } });
    setStep('progress');
  };

  const onDownloadErrors = async () => {
    const { url } = await createDownloadUrl(job.data.error_file.object_id);
    window.open(url, '_blank', 'noopener');
  };

  const busy = upload.isPending || createJob.isPending || validate.isPending;
  const jobData = job.data;
  const isRunning = jobData && !['completed', 'failed', 'rolled_back'].includes(jobData.status);
  const progressPct = jobData?.total_rows ? Math.round((jobData.processed_rows / jobData.total_rows) * 100) : 0;

  return (
    <Modal open={open} onClose={onClose} title="Import leads from CSV" size="lg"
      footer={step === 'review' ? (
        <>
          <button type="button" className="btn btn-cancel btn-sm-x" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-save btn-sm-x" disabled={execute.isPending || !preview?.summary.valid}
                  onClick={onRunImport}>
            {execute.isPending ? <><ButtonSpinner className="me-1" />Starting…</> : `Import ${preview?.summary.valid ?? 0} row${preview?.summary.valid === 1 ? '' : 's'}`}
          </button>
        </>
      ) : (
        <button type="button" className="btn btn-cancel btn-sm-x" onClick={onClose}>
          {step === 'progress' && !isRunning ? 'Done' : 'Close'}
        </button>
      )}
    >
      {step === 'upload' && (
        <>
          <div className="d-flex align-items-center justify-content-between gap-3 mb-3 p-2 px-3"
               style={{ background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 'var(--r)' }}>
            <div className="t-sm">
              <div className="fw-650">Not sure how to format your file?</div>
              <div className="ink-3 t-xs">Download a template with the columns this import understands.</div>
            </div>
            <button type="button" className="btn btn-soft btn-sm-x flex-shrink-0" onClick={downloadSampleCsv}>
              <i className="bi bi-file-earmark-arrow-down me-1" aria-hidden="true" />Sample CSV
            </button>
          </div>

          <Field label="CSV file" help="First name, Last name, Company, Email, Phone, Job Title, Industry, Country, Source, Status, Rating, Estimated Value and Owner Email are matched automatically by header name — only Last name and Company are required.">
            <input
              ref={fileInputRef} type="file" accept=".csv,text/csv" className="form-control"
              disabled={busy}
              onChange={(e) => onFile(e.target.files?.[0])}
            />
          </Field>
          {busy && (
            <div className="t-sm ink-3 mt-2">
              <ButtonSpinner className="me-2" />
              {upload.isPending ? `Uploading ${fileName}…` : validate.isPending ? 'Reading the file…' : 'Starting…'}
            </div>
          )}
        </>
      )}

      {step === 'review' && preview && (
        <>
          <div className="d-flex gap-3 mb-3">
            <div className="t-sm"><strong>{preview.total_rows}</strong> rows</div>
            <div className="t-sm" style={{ color: 'var(--emerald-600, #15803d)' }}><strong>{preview.summary.valid}</strong> valid</div>
            {preview.summary.invalid > 0 && <div className="t-sm" style={{ color: 'var(--rose-600, #be123c)' }}><strong>{preview.summary.invalid}</strong> invalid</div>}
            {preview.summary.duplicates > 0 && <div className="t-sm ink-3"><strong>{preview.summary.duplicates}</strong> possible duplicates</div>}
          </div>

          <Field label="Column mapping" help="Detected from the CSV header row.">
            <div className="d-flex flex-wrap gap-1">
              {Object.entries(preview.suggested_mapping).map(([header, field]) => (
                <span key={header} className="pill pill-gray t-2xs">{header} → {FIELD_LABEL[field] ?? field}</span>
              ))}
              {preview.unmapped_columns.map((header) => (
                <span key={header} className="pill pill-gray t-2xs" style={{ opacity: 0.5 }}>{header} (ignored)</span>
              ))}
            </div>
          </Field>

          <Field label="If a row matches an existing lead">
            <div className="d-flex flex-column gap-1">
              {STRATEGIES.map((s) => (
                <label key={s.value} className="d-flex align-items-start gap-2 t-sm">
                  <input type="radio" name="dup-strategy" checked={strategy === s.value} onChange={() => setStrategy(s.value)} className="mt-1" />
                  <span><strong>{s.label}</strong> — <span className="ink-3">{s.help}</span></span>
                </label>
              ))}
            </div>
          </Field>

          {preview.issues.length > 0 && (
            <div className="p-2 px-3 t-xs" style={{ background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 'var(--r)' }}>
              <div className="fw-650 mb-1">Row problems ({preview.summary.invalid} row{preview.summary.invalid === 1 ? '' : 's'})</div>
              {preview.issues.slice(0, 5).map((issue, i) => (
                <div key={i} className="ink-3">Row {issue.row}: {issue.message}</div>
              ))}
              {preview.issues.length > 5 && <div className="ink-4">…and more — a full error report is available after import.</div>}
            </div>
          )}
        </>
      )}

      {step === 'progress' && (
        <>
          <div className="mb-2 t-sm">
            {isRunning ? 'Importing…' : jobData?.status === 'completed' ? 'Import complete.' : 'The import failed.'}
          </div>
          {jobData?.total_rows > 0 && (
            <div className="mb-3" style={{ height: 8, borderRadius: 4, background: 'var(--surface-2)', overflow: 'hidden' }}>
              <div style={{ width: `${progressPct}%`, height: '100%', background: 'var(--brand-500, #6366f1)', transition: 'width .3s' }} />
            </div>
          )}
          {jobData && (
            <div className="d-flex flex-wrap gap-3 t-sm mb-2">
              <span>{jobData.processed_rows} / {jobData.total_rows} processed</span>
              <span style={{ color: 'var(--emerald-600, #15803d)' }}>{jobData.created_rows} created</span>
              <span>{jobData.updated_rows} updated</span>
              <span className="ink-3">{jobData.skipped_rows} skipped</span>
              {jobData.failed_rows > 0 && <span style={{ color: 'var(--rose-600, #be123c)' }}>{jobData.failed_rows} failed</span>}
            </div>
          )}
          {!isRunning && jobData?.error_file && (
            <button type="button" className="btn btn-soft btn-sm-x" onClick={onDownloadErrors}>
              <i className="bi bi-download me-1" aria-hidden="true" />Download error report
            </button>
          )}
        </>
      )}
    </Modal>
  );
}
